// src/components/CustomerAuthHero.js
// Shared hero panel for CustomerLogin + CustomerSignup
import { useMemo } from 'react'

const NAVY = '#0B1F3A'
const TEAL = '#2DD4B0'

const FEATURES = [
  { path: 'M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z', text: 'Track all your orders in one place' },
  { path: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8', text: 'Download receipts as PDF' },
  { path: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z', text: 'Message sellers directly' },
  { path: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', text: 'Free forever — no credit card' },
]

export default function CustomerAuthHero({ brand, subtitle }) {
  const heroPhoto = useMemo(() => {
    const slots = ['customer_signup_hero_1','customer_signup_hero_2','customer_signup_hero_3','customer_signup_hero_4','signup_hero_1','signup_hero_2']
    const available = slots.map(k => brand[k]).filter(Boolean)
    if (!available.length) return null
    return available[Math.floor(Math.random() * available.length)]
  }, []) // eslint-disable-line

  return (
    <div style={{
      background: 'linear-gradient(160deg, ' + NAVY + ' 0%, #0D2B4E 60%, #0A2240 100%)',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      padding: '48px 40px', position: 'relative', overflow: 'hidden', minHeight: '100vh',
    }}>
      {/* Background circles */}
      <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '320px', height: '320px', borderRadius: '50%', background: 'rgba(45,212,176,0.06)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-100px', left: '-60px', width: '380px', height: '380px', borderRadius: '50%', background: 'rgba(45,212,176,0.04)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '40%', right: '-20px', width: '160px', height: '160px', borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }} />

      {/* Brand */}
      <div style={{ position: 'relative' }}>
        {brand.logo_url
          ? <img src={brand.logo_url} alt={brand.name} style={{ height: '34px', objectFit: 'contain', marginBottom: '8px', display: 'block' }} />
          : <div style={{ fontSize: '24px', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', marginBottom: '6px' }}>{brand.name || 'PageConnect'}</div>
        }
        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>{subtitle || 'Track your orders, anytime'}</div>
      </div>

      {/* Hero image / placeholder */}
      <div style={{ position: 'relative', flex: 1, margin: '28px 0', borderRadius: '20px', overflow: 'hidden', minHeight: '260px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {heroPhoto ? (
          <>
            <img src={heroPhoto} alt="Customer portal" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(11,31,58,0.8) 0%, transparent 50%)' }} />
          </>
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '260px' }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="rgba(45,212,176,0.3)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0"/>
            </svg>
          </div>
        )}

        {/* Stats badge */}
        <div style={{ position: 'absolute', bottom: '16px', left: '16px', background: 'rgba(255,255,255,0.96)', borderRadius: '14px', padding: '10px 14px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
          <div style={{ fontSize: '10px', color: '#9CA3AF', marginBottom: '2px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Orders tracked</div>
          <div style={{ fontSize: '18px', fontWeight: 800, color: NAVY, lineHeight: 1 }}>12+</div>
          <div style={{ fontSize: '11px', color: TEAL, fontWeight: 700, marginTop: '2px' }}>Always up to date</div>
        </div>

        {/* Teal dot accent */}
        <div style={{ position: 'absolute', top: '16px', right: '16px', width: '10px', height: '10px', borderRadius: '50%', background: TEAL, boxShadow: '0 0 12px ' + TEAL }} />
      </div>

      {/* Feature list */}
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {FEATURES.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(45,212,176,0.12)', border: '1px solid rgba(45,212,176,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={f.path}/></svg>
            </div>
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.78)', lineHeight: 1.4 }}>{f.text}</span>
          </div>
        ))}
      </div>
    </div>
  )
}